import { Form } from 'module/ui/components/atoms/form';
import { PopoverSelect } from 'module/ui/components/molecules/popover-select';
import { PageTemplateSectionSource } from 'api/custom-section/types';
import { PageTemplateLite } from 'api/page-template/types';
import { flatten, keyBy } from 'lodash';
import {
  useGetAllSectionsInTemplatesQuery,
  useGetSuggestedSectionsForDerivedSectionQuery,
} from 'queries/templates/template-builder-queries';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CustomSectionNode } from '../../../../../types';
import type { Option } from '@rocketlane/ui';

const TriggerContent = styled.div`
  color: var(--text-placeholder);
`;
const Wrapper = styled(Form)`
  ${Form.Item} {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;
const sectionAsOption = (section: PageTemplateSectionSource) => ({
  label: section.pageTemplateSectionTitle,
  value: section.pageTemplateSectionId,
});

const allSectionsAsOptions = (sections: PageTemplateSectionSource[]) => {
  const allSectionsGroupedByTemplateId = sections?.reduce(
    (accumulator, section) => {
      const existingGroup = accumulator[section.pageTemplateId];

      if (existingGroup) {
        existingGroup.options.push(sectionAsOption(section));
      } else {
        accumulator[section.pageTemplateId] = {
          label: section.pageTemplateName,
          options: [sectionAsOption(section)],
        };
      }

      return accumulator;
    },
    {} as Record<
      number,
      {
        label: string;
        options: Option[];
      }
    >,
  );

  return flatten(Object.values(allSectionsGroupedByTemplateId));
};

interface Props {
  node: CustomSectionNode;
  value: PageTemplateSectionSource[];
  onChange: (sources: PageTemplateSectionSource[]) => void;
  pageTemplateSources: PageTemplateLite[];
}

export const DerivedSectionSourcesSelector = ({
  node,
  value,
  onChange,
  pageTemplateSources,
}: Props) => {
  const templateIds = useMemo(
    () => pageTemplateSources.map((source) => source.templateId),
    [pageTemplateSources],
  );
  const { data: suggestedSections } =
    useGetSuggestedSectionsForDerivedSectionQuery({
      templateIds,
      sectionName: node.attrs.title ?? '',
    });
  const { data: allSections } = useGetAllSectionsInTemplatesQuery(templateIds);
  const sectionLookup = useMemo(
    () => ({
      ...keyBy(suggestedSections ?? [], 'pageTemplateSectionId'),
      ...keyBy(allSections ?? [], 'pageTemplateSectionId'),
    }),
    [suggestedSections, allSections],
  );
  const options = useMemo(
    () => [
      {
        label: (
          <FormattedMessage id="company_page_templates.sections.sources.selector.group.suggested_blocks" />
        ),
        options: (suggestedSections ?? []).map(sectionAsOption),
      },
      ...allSectionsAsOptions(allSections ?? []),
    ],
    [suggestedSections, allSections],
  );

  const selected = useMemo(() => {
    return value.map(sectionAsOption);
  }, [value]);

  const onSourceChange = useCallback(
    (next: Option[]) => {
      onChange(
        next
          .map(({ value }) => sectionLookup[value])
          .filter((section) => section != null),
      );
    },
    [onChange, sectionLookup],
  );

  return (
    <Wrapper layout="vertical">
      <Form.Item
        name="sources"
        label={
          <FormattedMessage id="company_page_templates.sections.sources.selector.label" />
        }
        valuePropName=""
        getValueFromEvent={() => {}}
      >
        <PopoverSelect
          options={options}
          value={selected}
          onChange={onSourceChange}
          isMulti
          grouped
          title={
            <FormattedMessage id="company_page_templates.sections.sources.selector.label" />
          }
          triggerContent={
            <TriggerContent>
              <FormattedMessage id="company_page_templates.sections.sources.selector.placeholder" />
            </TriggerContent>
          }
        />
      </Form.Item>
    </Wrapper>
  );
};
